<template>
  <NuxtLayout>
    <main
      class="PageRedirect"
      :class="{ 'PageRedirect--message': showMessage }"
    >
      <template v-if="showMessage">
        <SkyImage
          src="https://images.grove.co/upload/f_auto,q_auto:low,fl_progressive,w_672,ar_4:3,c_fill,g_auto/v1591749331/global/Line%20Illustrations/no-image-available-4x3.jpg"
          alt="Line illustration of a hand holding a leaf"
        />
        <div class="PageRedirect_Content">
          <h3>This content is currently unavailable</h3>
          <p>
            The page you requested cannot be displayed at this time. It may be
            temporarily unavailable, the link you clicked may have expired, or
            you may not have permission to view this page.
          </p>
          <SkyLink to="/" display dark> Return Home </SkyLink>
        </div>
      </template>
      <SkyIcon v-else name="spinner" size="35px" />
    </main>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import { HandlingLevel, SeverityLevel } from '@/plugins/sentry.client'

/**
 * At this time Nuxt should never display a 404 because there are routes that
 * it does not yet handle. Instead we do a hard reload so the CloudFlare
 * can return the external page or 404 message.
 *
 * Since those layers are not always available on local or staging we must
 * guard against reload loops.
 */

definePageMeta({
  showNotificationBar: false,
  showFullFooter: false,
})

const showMessage = ref(false)
const { $sentry } = useNuxtApp()

onMounted(() => {
  try {
    const LAST_NOT_FOUND = 'groveco:web:notFound'
    const route = useRoute()
    const lastSession = JSON.parse(
      window.sessionStorage.getItem(LAST_NOT_FOUND) || '{}'
    )
    const ts = new Date().getTime()

    if (
      lastSession.ts &&
      ts - lastSession.ts < 10000 &&
      lastSession.fullPath === route.path
    ) {
      showMessage.value = true

      // Reload did not succeed in serving content to user; 404 shown.
      $sentry.captureException(new Error('404 error page displayed to user'), {
        tags: {
          level: SeverityLevel.FATAL,
          handling: HandlingLevel.WITH_MSG,
        },
      })
    } else {
      window.sessionStorage.setItem(
        LAST_NOT_FOUND,
        JSON.stringify({
          fullPath: route.path,
          ts,
        })
      )
      reloadNuxtApp()
    }
  } catch {
    // session storage not available
  }
})
</script>

<style lang="scss">
.PageRedirect {
  @include container-small;

  align-content: space-evenly;
  justify-items: center;
  min-height: 80vh;

  @include for-medium-up {
    --col: min(var(--container-max) / 2, 100% - var(--container-margin) * 2);

    &--message {
      grid-template-columns: 1fr var(--col) var(--col) 1fr;
      align-items: center;
    }

    &_Content {
      grid-column: 3;
    }
  }
}
</style>
